import React, { useState } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  makeStyles,
  Box,
  Typography,
  InputLabel,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import { InfoOutlined as InfoIcon } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import PopoverInfoSection from "./PopoverInfoSection";

const useStyles = (clientColor) =>
  makeStyles((theme) => ({
    ...customSelectStyle(theme, clientColor),
    sectionContainer: {
      border: `1px solid ${grayColor[8]}`,
      borderLeft: `4px solid ${clientColor || theme.palette.primary.main}`,
      borderRadius: theme.spacing(1),
      padding: theme.spacing(2),
      backgroundColor: grayColor[12],
      position: "relative",
    },
    sectionTitle: {
      fontSize: "0.95rem",
      fontWeight: 500,
      marginBottom: theme.spacing(2),
      color: clientColor || theme.palette.primary.main,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
    },
    sectionIcon: {
      fontSize: "1rem",
      opacity: 0.7,
      color: clientColor || theme.palette.primary.main,
    },
    iconButton: {
      padding: theme.spacing(0.75),
      color: theme.palette.text.secondary,
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
      },
    },
    infoButtonInSection: {
      position: "absolute",
      top: theme.spacing(1),
      right: theme.spacing(1),
      zIndex: 1000,
    },
    contentRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: theme.spacing(2),
      flexWrap: "wrap",
    },
    selectorControl: {
      minWidth: 170,
      "& > div": {
        "&:before": {
          borderBottomWidth: "1px !important",
          borderBottomColor: `${grayColor[4]}!important`,
        },
        "&:after": {
          borderBottomColor: `${clientColor}!important`,
        },
      },
      "& .MuiInputLabel-root": {
        "&.Mui-focused": {
          color: `${clientColor}!important`,
        },
      },
    },
  }));

export default function SectionModelRoute({
  value,
  onChange,
  clientColor,
  headerTitle,
  variants,
  selectedVariant,
  onVariantSelect,
  routePremises,
  routePremisesValue,
  onRoutePremisesChange,
  infoHtml,
}) {
  const classes = useStyles(clientColor)();
  const T_PREFIX = "rgm.cockpit.scenarioCreation";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  const [infoPopoverAnchor, setInfoPopoverAnchor] = useState(null);

  const handleInfoClick = (event) => {
    setInfoPopoverAnchor(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoPopoverAnchor(null);
  };

  return (
    <Box className={classes.sectionContainer}>
      {/* Info Button - Top Right */}
      {infoHtml && (
        <Tooltip title={t("route_model_info_tooltip")}>
          <IconButton
            onClick={handleInfoClick}
            className={`${classes.iconButton} ${classes.infoButtonInSection}`}
            size="small"
          >
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Typography className={classes.sectionTitle}>
        <AccountTreeIcon className={classes.sectionIcon} />
        {headerTitle}
      </Typography>
      <Box className={classes.contentRow}>
        {/* Dropdown Variants */}
        {variants && variants.length > 0 && (
          <FormControl size="small" className={classes.selectorControl}>
            <InputLabel id="model-route-variant-label">
              {t("model_variant")}
            </InputLabel>
            <Select
              labelId="model-route-variant-label"
              id="model-route-variant-select"
              value={selectedVariant || ""}
              onChange={(e) => onVariantSelect(e.target.value)}
              label={t("model_variant")}
              classes={{ select: classes.select }}
              MenuProps={{
                className: classes.selectMenu,
                PaperProps: {
                  className: classes.selectPaper,
                  style: { maxHeight: 300 },
                },
              }}
            >
              {variants.map((variant) => (
                <MenuItem
                  key={variant.variant_id}
                  value={variant.variant_id}
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  {variant.variant_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Dropdown Route Premises */}
        {routePremises && Object.keys(routePremises).length > 0 && (
          <FormControl size="small" className={classes.selectorControl}>
            <InputLabel id="route-premises-label">
              {t("route_premises")}
            </InputLabel>
            <Select
              labelId="route-premises-label"
              id="route-premises-select"
              value={routePremisesValue}
              onChange={onRoutePremisesChange}
              label={t("route_premises")}
              classes={{ select: classes.select }}
              MenuProps={{
                className: classes.selectMenu,
                PaperProps: {
                  className: classes.selectPaper,
                  style: { maxHeight: 300 },
                },
              }}
            >
              {Object.entries(routePremises).map(([key, label]) => (
                <MenuItem
                  key={key}
                  value={key}
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Dropdown Calculation Method */}
        <FormControl size="small" className={classes.selectorControl}>
          <InputLabel id="calculation-mode-label">
            {t("calculation_method")}
          </InputLabel>
          <Select
            labelId="calculation-mode-label"
            id="calculation-mode-select"
            value={value}
            onChange={onChange}
            label={t("calculation_method")}
            classes={{ select: classes.select }}
            MenuProps={{
              className: classes.selectMenu,
              PaperProps: {
                className: classes.selectPaper,
                style: { maxHeight: 300 },
              },
            }}
          >
            <MenuItem
              value="price_costbased"
              classes={{
                root: classes.selectMenuItem,
                selected: classes.selectMenuItemSelected,
              }}
            >
              {t("cost_based")}
            </MenuItem>
            <MenuItem
              value="seek_sellout_price"
              classes={{
                root: classes.selectMenuItem,
                selected: classes.selectMenuItemSelected,
              }}
            >
              {t("value_based")}
            </MenuItem>
            <MenuItem
              value="seek_nodes_margin"
              classes={{
                root: classes.selectMenuItem,
                selected: classes.selectMenuItemSelected,
              }}
            >
              {t("chain_margin")}
            </MenuItem>
            <MenuItem
              disabled={true}
              value="tbc_custom_selloutprice"
              classes={{
                root: classes.selectMenuItem,
                selected: classes.selectMenuItemSelected,
              }}
            >
              {"Custom Price - via TTV"}
            </MenuItem>
            <MenuItem
              disabled={true}
              value="tbc_custom_selloutprice"
              classes={{
                root: classes.selectMenuItem,
                selected: classes.selectMenuItemSelected,
              }}
            >
              {"Custom Price - Hybrid (cost/value based)"}
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Info Popover */}
      <PopoverInfoSection
        open={Boolean(infoPopoverAnchor)}
        anchorEl={infoPopoverAnchor}
        onClose={handleInfoClose}
        htmlContent={infoHtml}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </Box>
  );
}

SectionModelRoute.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  clientColor: PropTypes.string,
  headerTitle: PropTypes.string,
  variants: PropTypes.array,
  selectedVariant: PropTypes.number,
  onVariantSelect: PropTypes.func,
  routePremises: PropTypes.object,
  routePremisesValue: PropTypes.string,
  onRoutePremisesChange: PropTypes.func,
  infoHtml: PropTypes.string,
};
